import { useController } from 'react-hook-form';
import { Select } from 'ui/selectv2/Select';
import { SelectProps } from 'ui/selectv2/types';
import { FieldProps } from './types';

export type SelectV2FieldProps = Omit<SelectProps, 'onSelect' | 'value'> &
  FieldProps & {
    /**
     * optional prop that sets the expected type of the "value" prop provided to the component.
     * By default, the component expects the SelectProps['value'] type, which is an object with a label and value.
     * If, instead, "valueType" is set to "string", then the component will do some
     * under-the-hood conversion to make the string value work with the Select component.
     *
     * The "string" valueType may be useful for a select component used to select a single value from a list of strings
     * without needing to do any additional work in the form context to make this possible.
     */
    valueType?: 'string' | 'default';
  };

export const SelectV2Field = ({
  name,
  defaultValue,
  valueType = 'default',
  ...rest
}: SelectV2FieldProps) => {
  const {
    field: { onChange, value },
    fieldState: { error },
  } = useController({
    name,
    defaultValue,
  });

  const displayedValue = valueType === 'string' && value ? [{ value, label: value }] : value;

  return (
    <Select
      {...rest}
      errorMsg={error?.message}
      onSelect={
        valueType === 'string'
          ? ([option]) => onChange(option?.value)
          : ([option]) => onChange({ value: option?.value, label: option?.label })
      }
      value={displayedValue || []}
    />
  );
};
